import { useState, useRef, useEffect } from 'react'
import { Search as SearchIcon, FileText, Settings, Loader2, Play, Plus, Check, Film, HelpCircle, Sparkles, Brain } from 'lucide-react'
import { useHighlightsApi } from './useHighlightsApi'
import { useProfileMomentTypes } from './useProfileMomentTypes'
import TranscriptViewer from './TranscriptViewer'
import AliasesEditor from './AliasesEditor'
import { dedupAiKeywordAgainstLlm } from './dedupMoments'
import ClipPreview from './ClipPreview'

// AI key-moment filter chips — mirror the Key Moments tab so editors get
// the same vocabulary on both surfaces. Sourced from the sport-profile
// registry via useProfileMomentTypes(profileKey); hockey seed reproduces
// the old hardcoded list exactly. Filters affect only the 💫 KM list per
// video; the cue (caption) matches are untouched (they have no moment_type
// or source).
const KM_SOURCE_META = [
  { key: 'mux',             label: 'Mux',             color: '#fb923c' },
  { key: 'tbtv_ai_keyword', label: 'TBTV-AI keyword', color: '#a78bfa' },
  { key: 'tbtv_ai_llm',     label: 'TBTV-AI LLM',     color: '#22c55e' },
]

function fmtTime(ms) {
  const totalSec = Math.floor(ms / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

export default function TranscriptSearch({ reelCart, onSwitchTo, profileKey = 'hockey', profiles = [] }) {
  // KM chip vocabulary for this sport profile. Hockey fallback keeps the
  // Search tab byte-identical to pre-Phase-3 before the fetch resolves.
  // Note: KeyMomentsExplorer includes an extra '(other)' chip; TranscriptSearch
  // has always dropped it because KM filters here are for AI-tagged moments,
  // which by construction have a real type (never '(other)'). We keep that
  // behavior by filtering the returned list.
  const { momentTypes: allTypes } = useProfileMomentTypes(profileKey)
  const KM_TYPE_META = allTypes.filter(t => t.key !== '(other)')
  const api = useHighlightsApi()
  const [q, setQ] = useState('')
  const [mode, setMode] = useState('both') // 'exact' | 'fuzzy' | 'phonetic' | 'both'
  const [onlyKeyMoments, setOnlyKeyMoments] = useState(false)
  // KM-side filters — same shape as Key Moments tab. Empty Set = all.
  const [selectedTypes, setSelectedTypes] = useState(() => new Set())
  const [selectedSources, setSelectedSources] = useState(() => new Set())
  const [minScore, setMinScore] = useState(0)   // 0 = any
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [viewing, setViewing] = useState(null) // tbtv_id for transcript modal
  const [aliasesOpen, setAliasesOpen] = useState(false)
  const [previewing, setPreviewing] = useState(null) // { tbtv_id, startMs, endMs?, title }
  const [syntaxOpen, setSyntaxOpen] = useState(false)
  const inputRef = useRef(null)

  async function runSearch(e, { expand = false, agentic = false } = {}) {
    e?.preventDefault?.()
    if (!q.trim()) return
    setLoading(true)
    setErr(null)
    try {
      const r = await api.search(q.trim(), {
        limit: 50, mode, onlyInKeyMoments: onlyKeyMoments,
        momentType: selectedTypes.size > 0 ? Array.from(selectedTypes) : null,
        source: selectedSources.size > 0 ? Array.from(selectedSources) : null,
        minScore: minScore > 0 ? minScore : null,
        expand,
        agentic,
      })
      setResult(r)
    } catch (e) {
      setErr(e.message)
      setResult(null)
    } finally {
      setLoading(false)
    }
  }

  // Auto-rerun search when any KM filter changes AND we already have a
  // result loaded. Doesn't trigger on the first paint (result is null);
  // doesn't refetch when there's no query.
  useEffect(() => {
    if (!result || !q.trim()) return
    runSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTypes, selectedSources, minScore, onlyKeyMoments])

  function toggleType(key) {
    setSelectedTypes(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key); else next.add(key)
      return next
    })
  }
  function toggleSource(key) {
    setSelectedSources(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key); else next.add(key)
      return next
    })
  }

  return (
    <div>
      <div className="card" style={{ marginBottom: 12 }}>
        <form onSubmit={runSearch} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <SearchIcon size={16} style={{ color: '#888' }} />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search inside videos — names, plays, phrases… (try AND / OR / NOT / parens)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
            style={{ flex: 1, minWidth: 240, padding: '8px 10px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', fontSize: 14 }}
          />
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            title="Search mode"
            style={{ padding: '8px 10px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit' }}
          >
            <option value="both">Smart (exact + phonetic + fuzzy)</option>
            <option value="exact">Exact only</option>
            <option value="phonetic">Phonetic only (sound-alike)</option>
            <option value="fuzzy">Fuzzy only (substring)</option>
          </select>
          <button type="submit" disabled={loading || !q.trim()}
            style={{ padding: '8px 16px', background: '#3b82f6', border: 'none', borderRadius: 4, color: 'white', cursor: 'pointer', fontWeight: 500 }}>
            {loading ? <Loader2 size={14} className="spin" /> : 'Search'}
          </button>
          <button type="button" onClick={() => runSearch(null, { expand: true })} disabled={loading || !q.trim()}
            title="Ask Claude for synonyms + STT-mishearing aliases, then search — ~600ms extra latency, ~$0.0008 per call"
            style={{ padding: '8px 12px', background: '#1e1b4b', border: '1px solid #4338ca', borderRadius: 4, color: '#c4b5fd', cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Sparkles size={14} /> Smart expand
          </button>
          <button type="button" onClick={() => runSearch(null, { agentic: true })} disabled={loading || !q.trim()}
            title="Step 21 agentic pipeline: Llama intent + Vector semantic + Llama reranker with per-clip rationale. ~15-20s on first call; cached 24hr; ~$0.001 per call"
            style={{ padding: '8px 12px', background: '#0f1d3a', border: '1px solid #1d4ed8', borderRadius: 4, color: '#93c5fd', cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Brain size={14} /> Agentic
          </button>
          <button type="button" onClick={() => setSyntaxOpen(true)} title="Search syntax cheatsheet"
            style={{ padding: '8px 10px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: '#888', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <HelpCircle size={14} />
          </button>
          <button type="button" onClick={() => setAliasesOpen(true)} title="Manage spelling aliases"
            style={{ padding: '8px 12px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Settings size={14} /> Aliases
          </button>
        </form>

        {/* Filter row: Mux Robots key-moment restriction */}
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: '#888', flexWrap: 'wrap' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }} title="Restrict cue (caption) matches to those that fall inside a Find-Key-Moments window. Affects the caption results, not the 💫 AI key moment matches.">
            <input
              type="checkbox"
              checked={onlyKeyMoments}
              onChange={(e) => setOnlyKeyMoments(e.target.checked)}
            />
            <span>💫 Only inside key moments (cues)</span>
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} title="Drop AI key moment matches below this quality score. Doesn't affect cue matches.">
            <span>Min KM score:</span>
            <select
              value={minScore}
              onChange={(e) => setMinScore(parseFloat(e.target.value))}
              style={{ padding: '3px 6px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', fontSize: 12 }}
            >
              <option value={0}>any</option>
              <option value={0.5}>0.5</option>
              <option value={0.6}>0.6</option>
              <option value={0.7}>0.7</option>
              <option value={0.8}>0.8</option>
              <option value={0.9}>0.9</option>
            </select>
          </label>
        </div>

        {/* AI key-moment source + type chip strips. Affect the 💫 KM
            section only — cue matches are untouched. Mirror the Key
            Moments tab so the editor has the same vocabulary in both
            surfaces. */}
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: '#666', marginRight: 4 }}>KM source:</span>
          <Chip
            label="All"
            active={selectedSources.size === 0}
            onClick={() => setSelectedSources(new Set())}
            color="#bfdbfe"
          />
          {KM_SOURCE_META.map(s => (
            <Chip
              key={s.key}
              label={s.label}
              active={selectedSources.has(s.key)}
              onClick={() => toggleSource(s.key)}
              color={s.color}
            />
          ))}
        </div>
        {/* KM type chip strip — grouped by sport profile. Search is
            catalog-wide, so every enabled profile contributes its own
            row of chips even when the header dropdown shows a different
            active sport. Falls back to the single-profile strip when
            the profiles list hasn't loaded yet. The active profile's
            row gets a bolder label; other rows stay dim but usable. */}
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: '#666', marginRight: 4 }}>KM type:</span>
            <Chip
              label="All"
              active={selectedTypes.size === 0}
              onClick={() => setSelectedTypes(new Set())}
              color="#bfdbfe"
            />
          </div>
          {(() => {
            const groups = profiles.length > 0
              ? profiles.map(p => ({
                  profile_key: p.profile_key,
                  display_name: p.display_name,
                  types: (Array.isArray(p.moment_types) ? p.moment_types : [])
                    .filter(t => t.key !== '(other)'),
                }))
              : [{ profile_key: profileKey, display_name: null, types: KM_TYPE_META }]
            return groups.map(g => {
              if (g.types.length === 0) return null
              const activeProfile = g.profile_key === profileKey
              return (
                <div key={g.profile_key} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {g.display_name && (
                    <span style={{
                      fontSize: 10, minWidth: 68, color: activeProfile ? '#bfdbfe' : '#666',
                      fontWeight: activeProfile ? 600 : 500,
                      textTransform: 'uppercase', letterSpacing: 0.4,
                    }}>{g.display_name}</span>
                  )}
                  {g.types.map(t => (
                    <Chip
                      key={`${g.profile_key}:${t.key}`}
                      label={t.label}
                      active={selectedTypes.has(t.key)}
                      onClick={() => toggleType(t.key)}
                      color={t.color}
                    />
                  ))}
                </div>
              )
            })
          })()}
        </div>
      </div>

      {err && (
        <div className="card" style={{ color: 'crimson', marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1 }}>{err}</div>
          {/^.*invalid boolean query/i.test(err) && (
            <button onClick={() => setSyntaxOpen(true)}
              style={{ padding: '2px 8px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: '#bfdbfe', cursor: 'pointer', fontSize: 12 }}>
              Syntax help
            </button>
          )}
        </div>
      )}

      {reelCart && reelCart.cart.length > 0 && (
        <div className="card" style={{ marginBottom: 12, background: '#1a2538', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{reelCart.cart.length} segment{reelCart.cart.length === 1 ? '' : 's'} in reel cart</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => onSwitchTo?.('reels')}
              style={{ padding: '6px 14px', background: '#3b82f6', border: 'none', borderRadius: 4, color: 'white', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 500 }}>
              <Film size={14} /> Open reel builder
            </button>
            <button onClick={() => reelCart.clear()}
              style={{ padding: '6px 12px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer' }}>
              Clear
            </button>
          </div>
        </div>
      )}

      {result && (
        <>
          <div style={{ marginBottom: 12, color: '#888', fontSize: 13, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <span>
              {result.video_count} videos · {result.cue_match_count} cue match{result.cue_match_count === 1 ? '' : 'es'}
              {result.key_moment_match_count > 0 && <> · {result.key_moment_match_count} AI moment match{result.key_moment_match_count === 1 ? '' : 'es'}</>}
              {result.title_match_count > 0 && <> · {result.title_match_count} title match{result.title_match_count === 1 ? '' : 'es'}</>}
              {' '}for "{result.query}"
              {result.mode !== 'both' && ` · mode=${result.mode}`}
              {result.expanded_by_llm && (
                <span title={result.llm_expansion?.from_cache ? 'Served from KV cache (no Claude call)' : 'Fresh Claude call (cached for 30d)'}
                  style={{ marginLeft: 8, fontSize: 11, padding: '1px 6px', borderRadius: 10, background: '#1e1b4b', color: '#c4b5fd', fontWeight: 600 }}>
                  ✨ {result.llm_expansion?.from_cache ? 'cached' : 'expanded'}
                </span>
              )}
            </span>
            {result.expanded_terms.length > 1 && (
              <span style={{ fontSize: 12 }}>
                expanded: {result.expanded_terms.map(t => <code key={t} style={{ marginLeft: 4, padding: '1px 6px', background: '#222', borderRadius: 3 }}>{t}</code>)}
              </span>
            )}
          </div>

          {/* Step 19: chip strip showing what Claude added. Clicking a chip
              re-runs the search with that term as the query — handy for
              iterating off Claude's suggestions. */}
          {result.expanded_by_llm && result.llm_expansion && (
            <div className="card" style={{ marginBottom: 12, fontSize: 12, padding: '10px 14px', background: 'rgba(167,139,250,0.04)', border: '1px solid #2a2a2a' }}>
              <div style={{ color: '#a5b4fc', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, fontSize: 11 }}>
                ✨ Claude expanded with
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {(result.llm_expansion.phrasings || []).map((p, i) => (
                  <button key={`ph-${i}`} onClick={() => { setQ(p); runSearch() }} title="Search this phrase"
                    style={{ padding: '2px 8px', borderRadius: 999, background: 'transparent', border: '1px solid #4338ca', color: '#c4b5fd', cursor: 'pointer', fontSize: 11, fontWeight: 500 }}>
                    {p}
                  </button>
                ))}
                {(result.llm_expansion.aliases || []).map((a, i) => (
                  <button key={`al-${i}`} onClick={() => setAliasesOpen(true)} title="Open Aliases editor to make permanent"
                    style={{ padding: '2px 8px', borderRadius: 999, background: 'transparent', border: '1px dashed #4338ca', color: '#bfdbfe', cursor: 'pointer', fontSize: 11, fontWeight: 500 }}>
                    {a.term} → {(a.alternatives || []).slice(0, 3).join(', ')}
                  </button>
                ))}
              </div>
            </div>
          )}

          {result.llm_error && (
            <div className="card" style={{ marginBottom: 12, color: '#fbbf24', fontSize: 12, background: 'rgba(251,191,36,0.06)', border: '1px solid #4a3a1c' }}>
              ✨ Smart expand skipped: {result.llm_error} — showing unexpanded results.
            </div>
          )}

          {result.videos.length === 0 && !loading && (
            <div className="card" style={{ color: '#888', textAlign: 'center', padding: 24 }}>
              No matches.
            </div>
          )}

          {/* Step 21 Phase C: agentic pipeline output */}
          {result.intent && (
            <div className="card" style={{ marginBottom: 12, padding: '10px 14px', background: 'rgba(29,78,216,0.06)', border: '1px solid #1d4ed8' }}>
              <div style={{ fontSize: 11, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                🧠 Agentic understanding
              </div>
              <div style={{ fontSize: 12, color: '#bbb', display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {result.intent.entities?.length > 0 && (
                  <span>entities: {result.intent.entities.map(e => `${e.raw}(${e.kind})`).join(', ')}</span>
                )}
                {result.intent.event_types?.length > 0 && (
                  <span>events: {result.intent.event_types.join(', ')}</span>
                )}
                {result.intent.semantic_hints?.length > 0 && (
                  <span>hints: {result.intent.semantic_hints.join(', ')}</span>
                )}
                {result.intent.ranking_intent && result.intent.ranking_intent !== 'best_of' && (
                  <span>ranking: {result.intent.ranking_intent}</span>
                )}
                {typeof result.intent.confidence === 'number' && (
                  <span>conf: {result.intent.confidence.toFixed(2)}</span>
                )}
              </div>
              {result.pipeline?.rerank?.applied && (
                <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
                  Reranked by {result.pipeline.rerank.model || '?'} {result.pipeline.rerank.cache_hit ? '(cache hit)' : '(fresh)'} · stage timings: s1 {result.pipeline.stage1_intent_ms || 0}ms · s2 {Math.max(result.pipeline.stage2_lexical_ms || 0, result.pipeline.stage2_semantic_ms || 0)}ms · s3 {result.pipeline.stage3_rerank_ms || 0}ms
                </div>
              )}
            </div>
          )}

          {Array.isArray(result.ranked_clips) && result.ranked_clips.length > 0 && (
            <div className="card" style={{ marginBottom: 12, background: 'rgba(29,78,216,0.04)', border: '1px solid #1d4ed8' }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #1d4ed8', fontSize: 11, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: 0.5, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                🧠 Top ranked clips ({result.ranked_clips.length})
              </div>
              <div>
                {result.ranked_clips.slice(0, 15).map((c, i) => (
                  <div key={`${c.clip_id}-${i}`}
                    style={{ padding: '10px 14px', borderBottom: '1px solid #1a1a1a', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <div style={{ flexShrink: 0, padding: '2px 8px', borderRadius: 10, background: c.relevance >= 0.85 ? '#1e3a26' : c.relevance >= 0.6 ? '#3a3014' : '#3a1414', color: c.relevance >= 0.85 ? '#86efac' : c.relevance >= 0.6 ? '#facc15' : '#fca5a5', fontWeight: 600, fontSize: 11 }}>
                      {(c.relevance ?? 0).toFixed(2)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: '#e5e7eb' }}>
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: c.kind === 'km' ? '#1e1b4b' : c.kind === 'cue' ? '#1f2937' : '#1e3a8a', color: '#bfdbfe', marginRight: 6 }}>{c.kind}</span>
                        {c.text || c.title}
                      </div>
                      {c.rationale && (
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3, fontStyle: 'italic' }}>
                          ↳ {c.rationale}
                        </div>
                      )}
                      {Array.isArray(c.caught_by) && c.caught_by.length > 0 && (
                        <div style={{ fontSize: 10, color: '#666', marginTop: 3, fontFamily: 'monospace' }}>
                          caught_by: {c.caught_by.join(' + ')} · from {c.video_title?.slice(0, 60) || c.tbtv_id}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.videos.map(video => (
            <div key={video.tbtv_id} className="card" style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                {video.thumbnail_url ? (
                  <img src={video.thumbnail_url} alt="" style={{ width: 96, height: 54, objectFit: 'cover', borderRadius: 4, background: '#222', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 96, height: 54, background: '#222', borderRadius: 4, flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span>{video.title || '(untitled)'}</span>
                    {video.title_match && (
                      <span title="The video's editorial title matches the query"
                        style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#1e3a8a', color: '#bfdbfe', fontWeight: 600 }}>
                        title match
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#777', fontFamily: 'monospace', marginTop: 2 }}>
                    {video.tbtv_id} · {video.match_count} cue match{video.match_count === 1 ? '' : 'es'}
                    {video.key_moment_match_count > 0 && (
                      <> · {video.key_moment_match_count} AI key moment{video.key_moment_match_count === 1 ? '' : 's'}</>
                    )}
                  </div>
                </div>
                <button onClick={() => setViewing(video.tbtv_id)}
                  style={{ alignSelf: 'flex-start', padding: '4px 10px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <FileText size={12} /> View transcript
                </button>
              </div>

              {/* AI key moment text matches — rendered above cue matches because
                  curated AI moments are higher-signal than raw transcript hits.
                  Same Play + Add-to-cart affordances as cue rows. */}
              {Array.isArray(video.key_moment_matches) && video.key_moment_matches.length > 0 && (() => {
                // Step 25g: dedup KW matches contained in LLM spans for THIS
                // video. The dedup helper keys on tbtv_id; KMs in the search
                // payload don't carry it, so we stamp the parent video's id
                // before calling the helper.
                const dedupedKms = dedupAiKeywordAgainstLlm(
                  video.key_moment_matches.map(km => ({ ...km, tbtv_id: video.tbtv_id }))
                )
                if (dedupedKms.length === 0) return null
                return (
                <div style={{ marginBottom: 10, padding: 8, background: '#0f172a', border: '1px solid #1e293b', borderRadius: 4 }}>
                  <div style={{ fontSize: 11, color: '#a5b4fc', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    💫 AI key moments matching "{result.query}"
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'auto auto 70px 1fr auto', columnGap: 12, rowGap: 6, fontSize: 13, lineHeight: 1.55, alignItems: 'center' }}>
                    {dedupedKms.map((km, i) => (
                      <KeyMomentMatch
                        key={`km-${km.ord}-${i}`}
                        km={km}
                        onPlay={() => setPreviewing({ tbtv_id: video.tbtv_id, startMs: km.start_ms, endMs: km.end_ms, title: km.title || video.title })}
                        inCart={reelCart?.has?.(video.tbtv_id, km.start_ms)}
                        onAddToCart={() => reelCart?.add?.({
                          tbtv_id: video.tbtv_id, start_ms: km.start_ms,
                          // Step 22: KM matches carry an end_ms; cue matches
                          // don't (transcript cue spans are already short
                          // and the legacy ±pre/post window suits them).
                          end_ms: km.end_ms,
                          _origin: {
                            kind: 'search', query: q.trim(), mode,
                            via: 'key_moment_text', moment_type: km.moment_type,
                            source: km.source, detector: km.detector,
                          },
                        })}
                        onRemoveFromCart={() => reelCart?.remove?.(video.tbtv_id, km.start_ms)}
                      />
                    ))}
                  </div>
                </div>
                )
              })()}

              {video.title_match && video.matches.length === 0 && video.key_moment_match_count === 0 && (
                <div style={{ fontSize: 12, color: '#888', padding: '8px 4px', fontStyle: 'italic' }}>
                  Matched on the video title — no transcript cue or AI key moment text matched.
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'auto auto 70px 1fr 70px', columnGap: 12, rowGap: 4, fontSize: 13, lineHeight: 1.55, alignItems: 'center' }}>
                {video.matches.map((m, i) => (
                  <Match
                    key={`${m.start_ms}-${i}`}
                    match={m}
                    tbtv_id={video.tbtv_id}
                    onPlay={() => setPreviewing({ tbtv_id: video.tbtv_id, startMs: m.start_ms, title: video.title })}
                    inCart={reelCart?.has?.(video.tbtv_id, m.start_ms)}
                    onAddToCart={() => reelCart?.add?.({
                      tbtv_id: video.tbtv_id, start_ms: m.start_ms,
                      // Context the reel builder uses to auto-generate a default title.
                      // Stamped at the moment of cart-add so it survives tab switches.
                      _origin: { kind: 'search', query: q.trim(), mode },
                    })}
                    onRemoveFromCart={() => reelCart?.remove?.(video.tbtv_id, m.start_ms)}
                  />
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      {!result && !loading && !err && (
        <div className="card" style={{ color: '#888', textAlign: 'center', padding: 24 }}>
          Search caption text across all active VOD with indexed captions.
          <div style={{ marginTop: 8, fontSize: 12, maxWidth: 640, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 }}>
            <strong>Smart</strong> runs three layers in parallel:
            <span style={{ color: '#22c55e' }}> exact</span> match →
            <span style={{ color: '#a78bfa' }}> phonetic</span> sound-alike →
            <span style={{ color: '#888' }}> trigram</span> substring.
            <br />
            Add <strong>aliases</strong> for specific names STT consistently mishears
            (e.g. "kazrowski" → "kaczorowski") if phonetic alone misses them.
          </div>
        </div>
      )}

      {viewing && (
        <TranscriptViewer
          tbtv_id={viewing}
          language="en"
          onClose={() => setViewing(null)}
        />
      )}
      {aliasesOpen && (
        <AliasesEditor onClose={() => setAliasesOpen(false)} />
      )}
      {syntaxOpen && <SyntaxHelpModal onClose={() => setSyntaxOpen(false)} />}
      {previewing && (
        <ClipPreview
          tbtv_id={previewing.tbtv_id}
          startMs={previewing.startMs}
          endMs={previewing.endMs}
          title={previewing.title}
          onClose={() => setPreviewing(null)}
        />
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } } .spin { animation: spin 1s linear infinite; } mark { background: #3b82f6; color: white; padding: 0 2px; border-radius: 2px; }`}</style>
    </div>
  )
}

// Cheatsheet for the boolean search syntax. Opens from the `?` icon next
// to the search bar and from the inline parse-error banner.
const SYNTAX_EXAMPLES = [
  { q: 'Caden Kaczorowski',                  meaning: 'Both words must appear in the cue/KM (implicit AND, alias-expanded per word).' },
  { q: '"Caden Kaczorowski"',                meaning: 'Match the exact phrase, adjacent words only (use quotes for strict).' },
  { q: '"Caden Kaczorowski" AND "Aiden Rice"', meaning: 'Both exact phrases must appear in the same cue/KM.' },
  { q: 'Murphy AND goal',                    meaning: 'Both tokens explicit; same as multi-word default.' },
  { q: 'Murphy OR Kaczorowski',              meaning: 'Either token (explicit OR).' },
  { q: 'Murphy AND NOT penalty',             meaning: 'Murphy hits excluding any with the word "penalty".' },
  { q: '(Murphy OR Kaczorowski) AND goal',   meaning: 'Grouping with parens.' },
]
function SyntaxHelpModal({ onClose }) {
  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
      <div style={{ background: '#111', border: '1px solid #2a2a2a', borderRadius: 8, width: 'min(720px, 96vw)', maxHeight: '88vh', overflow: 'auto' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #2a2a2a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Search syntax</div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', fontSize: 18, padding: 0 }}>×</button>
        </div>
        <div style={{ padding: 16, fontSize: 13, lineHeight: 1.55 }}>
          <p style={{ marginTop: 0 }}>
            <strong>Multi-word queries are implicit AND.</strong> Typing <code>Caden Kaczorowski</code> finds cues containing BOTH words (in any order), with per-word alias expansion. For strict in-order phrase match, wrap in double quotes: <code>"Caden Kaczorowski"</code>. Add <code>AND</code> / <code>OR</code> / <code>NOT</code> + parens (uppercase, whitespace-separated) to compose boolean queries. Names like <em>"Mandy"</em> are NOT parsed as boolean since operators must be whole tokens.
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: '#aaa' }}>Query</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: '#aaa' }}>Meaning</th>
              </tr>
            </thead>
            <tbody>
              {SYNTAX_EXAMPLES.map((e, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #1a1a1a' }}>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace', color: '#bfdbfe' }}>{e.q}</td>
                  <td style={{ padding: '6px 8px', color: '#bbb' }}>{e.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ marginTop: 16, fontSize: 12, color: '#888' }}>
            All boolean compositions work across the existing engine layers: <span style={{ color: '#22c55e' }}>exact</span> · <span style={{ color: '#a78bfa' }}>phonetic</span> · <span style={{ color: '#666' }}>trigram</span> · 💫 AI key moments. Alias expansion runs per term, so canonical/alias mappings still apply.
          </p>
        </div>
      </div>
    </div>
  )
}

// Filter chip — colored pill, dimmer when inactive. Same shape used in
// KeyMomentsExplorer; local copy to avoid coupling the two surfaces.
function Chip({ label, active, onClick, color }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '3px 9px',
        background: active ? color : 'transparent',
        border: '1px solid ' + (active ? color : '#333'),
        borderRadius: 999,
        color: active ? '#000' : color,
        cursor: 'pointer',
        fontSize: 11,
        fontWeight: 500,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}>
      {label}
    </button>
  )
}

function matchSourceColor(source) {
  if (source === 'exact')    return '#22c55e' // green
  if (source === 'phonetic') return '#a78bfa' // purple — sounds-alike layer
  return '#888'                                // trigram / other
}

// Row in the AI key moment matches grid. Mirrors `Match` for the
// Play / Add-to-cart affordances so cart adds work identically.
function KeyMomentMatch({ km, onPlay, inCart, onAddToCart, onRemoveFromCart }) {
  const matchedIn = Array.isArray(km.matched_in) ? km.matched_in : []
  const narrative = km.audible_narrative || km.visual_narrative
  return (
    <>
      <button onClick={onPlay} title="Preview key moment (±10s)"
        style={{ background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', padding: '3px 6px', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
        <Play size={11} />
      </button>
      <button
        onClick={inCart ? onRemoveFromCart : onAddToCart}
        title={inCart ? 'Remove from reel' : 'Add to reel'}
        style={{
          background: inCart ? '#3b82f6' : 'transparent',
          border: '1px solid ' + (inCart ? '#3b82f6' : '#333'),
          borderRadius: 4,
          color: inCart ? 'white' : 'inherit',
          cursor: 'pointer', padding: '3px 6px',
          display: 'inline-flex', alignItems: 'center', fontSize: 11,
        }}>
        {inCart ? <Check size={11} /> : <Plus size={11} />}
      </button>
      <div style={{ color: '#666', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
        {fmtTime(km.start_ms)}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 500 }}>{km.title || '(untitled moment)'}</div>
        {narrative && (
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2, lineHeight: 1.4 }}>
            {narrative}
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
          {km.moment_type && (
            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#1f2937', color: '#bbb', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
              {km.moment_type.replace('_', ' ')}
            </span>
          )}
          {km.quality_score != null && (
            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#1f2937', color: '#bbb', fontWeight: 600 }}>
              {km.quality_score.toFixed(2)}
            </span>
          )}
          {km.source === 'tbtv_ai' && (
            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#1e1b4b', color: '#c4b5fd', fontWeight: 600 }}>
              ai{km.detector ? `·${km.detector}` : ''}
            </span>
          )}
          {km.source === 'mux' && (
            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#1f2937', color: '#888', fontWeight: 600 }}>
              mux
            </span>
          )}
          {matchedIn.map(field => (
            <span key={field} title={`Matched in ${field}`}
              style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#1e3a8a', color: '#bfdbfe', fontWeight: 600 }}>
              {field}
            </span>
          ))}
        </div>
      </div>
      <div style={{ fontSize: 10, color: '#a78bfa', textAlign: 'right' }}>key moment</div>
    </>
  )
}

function Match({ match, onPlay, inCart, onAddToCart, onRemoveFromCart }) {
  return (
    <>
      <button onClick={onPlay} title="Preview clip (±10s)"
        style={{ background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', padding: '3px 6px', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
        <Play size={11} />
      </button>
      <button
        onClick={inCart ? onRemoveFromCart : onAddToCart}
        title={inCart ? 'Remove from reel' : 'Add to reel'}
        style={{
          background: inCart ? '#3b82f6' : 'transparent',
          border: '1px solid ' + (inCart ? '#3b82f6' : '#333'),
          borderRadius: 4,
          color: inCart ? 'white' : 'inherit',
          cursor: 'pointer',
          padding: '3px 6px',
          display: 'inline-flex',
          alignItems: 'center',
          fontSize: 11,
        }}>
        {inCart ? <Check size={11} /> : <Plus size={11} />}
      </button>
      <div style={{ color: '#666', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {fmtTime(match.start_ms)}
        {match.in_key_moment && (
          <span title="Inside a Mux Robots key moment" style={{ fontSize: 11 }}>💫</span>
        )}
      </div>
      {/* Phonetic-only matches don't have an FTS5 snippet (the snippet would
          be over the phonetic codes, which aren't human readable). Fall back
          to the raw text. */}
      {match.excerpt
        ? <div style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: match.excerpt }} />
        : <div style={{ whiteSpace: 'pre-wrap' }}>{match.text}</div>}
      <div style={{ fontSize: 10, color: matchSourceColor(match.source), textAlign: 'right' }}>
        {match.source}
      </div>
    </>
  )
}
